/**
 * User-aware logging utility
 *
 * All log messages are prefixed with user info for easier debugging in multi-user environments.
 *
 * Usage:
 *   import { logger } from '../utils/logger';
 *   logger.info('RAG SELECTION', 'Document selected');
 *   // Output: [USER:user@example.com] [RAG SELECTION] Document selected
 */

const getUserPrefix = () => {
    try {
        // Try to get user info from localStorage
        const userStr = localStorage.getItem('user');
        if (userStr) {
            const user = JSON.parse(userStr);
            const email = user.email || user.username || 'unknown';
            return `[USER:${email}]`;
        }
        return '[USER:anonymous]';
    } catch (e) {
        return '[USER:unknown]';
    }
};

const formatMessage = (category, message) => {
    const userPrefix = getUserPrefix();
    if (category) {
        return `${userPrefix} [${category}] ${message}`;
    }
    return `${userPrefix} ${message}`;
};

export const logger = {
    /**
     * Log an info message
     * @param {string} category - Category/module name (e.g., 'RAG SELECTION', 'CHAT')
     * @param {string} message - The log message
     * @param {...any} args - Additional arguments to log
     */
    info: (category, message, ...args) => {
        if (args.length > 0) {
            console.log(formatMessage(category, message), ...args);
        } else {
            console.log(formatMessage(category, message));
        }
    },

    /**
     * Log a warning message
     */
    warn: (category, message, ...args) => {
        if (args.length > 0) {
            console.warn(formatMessage(category, message), ...args);
        } else {
            console.warn(formatMessage(category, message));
        }
    },

    /**
     * Log an error message
     */
    error: (category, message, ...args) => {
        if (args.length > 0) {
            console.error(formatMessage(category, message), ...args);
        } else {
            console.error(formatMessage(category, message));
        }
    },

    /**
     * Log a debug message
     */
    debug: (category, message, ...args) => {
        if (args.length > 0) {
            console.debug(formatMessage(category, message), ...args);
        } else {
            console.debug(formatMessage(category, message));
        }
    },

    /**
     * Log with a custom console method
     */
    log: (category, message, ...args) => {
        if (args.length > 0) {
            console.log(formatMessage(category, message), ...args);
        } else {
            console.log(formatMessage(category, message));
        }
    }
};

export default logger;
